import { useState, useEffect } from 'react';
import {
  Form,
  FormItem,
  InputHashtag,
  ButtonArea,
  Button,
  Tooltip,
  Heading,
  Text,
  Badge,
  Input,
  IconButton,
  RadioGroup,
  Switch,
} from '../../../components/base';
import {
  AuthorityType,
  EOpenScopeType,
  IPluginPromptData,
  ManagedMember,
} from '../../../libs/models/studio/PluginPromptList';
import { UserData } from '../../../libs/models/users/UsersState';
import UserSelector from '../../../components/UserSelector';

const options = [
  {
    value: 'Public',
    label: '전체공개',
  },
  {
    value: 'Part',
    label: '일부공개',
    disabled: false,
  },
  {
    value: 'Private',
    label: '나만보기',
  },
];

interface IAionuCreateReg {
  formData: IPluginPromptData;
  authority: AuthorityType;
  setFormData: React.Dispatch<React.SetStateAction<IPluginPromptData>>;
}

const AionuCreateReg: React.FC<IAionuCreateReg> = ({ formData, authority, setFormData }) => {
    const [selectedUsers, setSelectedUsers] = useState<UserData[]>([]);

  const [managedUsers, setManagedUsers] = useState<UserData[]>(
    formData.managedMembers?.filter((m) => m.authorityType == AuthorityType.Contributor).map((m) => m.user) ?? [],
  );
  
  const disabledUsers =
    formData.managedMembers?.filter((m) => m.authorityType == AuthorityType.Owner).map((m) => m.user) ?? [];

  const disabledShare =
    formData.managedMembers?.map((m) => m.user) ?? [];

  useEffect(() => {
    if (authority == AuthorityType.Contributor) {
      options.forEach((option) => {
        option.disabled = true;
      });
    }
  }, [authority]);

  useEffect(() => {
  if (formData.selectedMembers) {
    setSelectedUsers(formData.selectedMembers);
  }
}, [formData.selectedMembers]);

 useEffect(() => {
  if (formData.managedMembers) {
    setManagedUsers( formData.managedMembers?.filter((m) => m.authorityType == AuthorityType.Contributor).map((m) => m.user) ?? []);
  }
}, [formData.managedMembers]);

  useEffect(() => {
    // 컴포넌트가 마운트될 때 conversationStarters가 비어있으면 기본 값 설정
    if (!formData.conversationStarters || formData.conversationStarters.length === 0) {
      setFormData((prev) => ({
        ...prev,
        conversationStarters: [''], // 기본 값
      }));
    }
  }, [formData.conversationStarters, setFormData]);

  // 대화스타터 추가 (최대 5개)
  const addQuestion = () => {
    if ((formData.conversationStarters?.length ?? 0) < 5) {
      setFormData((prev) => ({
        ...prev,
        conversationStarters: [...(prev.conversationStarters ?? []), ''],
      }));
    }
  };

  // 대화스타터 삭제
  const removeQuestion = (index: number) => {
    setFormData((prev) => ({
      ...prev,
      conversationStarters: prev.conversationStarters?.filter((_, i) => i !== index),
    }));
  };

  // 대화스타터 내용 변경
  const handleQuestionChange = (index: number, content: string) => {
    setFormData((prev) => ({
      ...prev,
      conversationStarters: prev.conversationStarters?.map((q, i) => (i === index ? content : q)),
    }));
  };

  const handleRadioChange = (_event: React.ChangeEvent<HTMLInputElement>, value: string) => {
    let openScope: EOpenScopeType | undefined;
    switch (value) {
      case 'Public':
        openScope = EOpenScopeType.Public;
        break;
      case 'Private':
        openScope = EOpenScopeType.Private;
        break;
      case 'Part':
        openScope = EOpenScopeType.Part;
        break;
      default:
        openScope = undefined;
    }

    if (openScope !== undefined) {
      setFormData((prev) => ({ ...prev, openScope }));
    }
  };

  const handleHashtagChange = (newHashtags: string[]) => {
    setFormData((prev) => ({ ...prev, tags: newHashtags }));
  };

  // 관리자 추가 상태 관리
  const handleManageSelect = (users: UserData[]) => {
    const owner = formData.managedMembers?.filter((m) => m.authorityType == AuthorityType.Owner) ?? [];
    const updated: ManagedMember[] = users.map((u) => {
      return { user: u, authorityType: AuthorityType.Contributor };
    });
    setFormData((prev) => ({ ...prev, managedMembers: [...owner, ...updated] }));
    setManagedUsers(users);
  };

  // 관리자 제거 상태 관리
  const handleManageRemove = (updatedUsers: UserData[]) => {
    const owner = formData.managedMembers?.filter((m) => m.authorityType == AuthorityType.Owner) ?? [];
    const updated: ManagedMember[] = updatedUsers.map((u) => {
      return { user: u, authorityType: AuthorityType.Contributor };
    });
    setFormData((prev) => ({ ...prev, managedMembers: [...owner, ...updated] }));
    setManagedUsers(updatedUsers);
  };

    // 공유 받은 사람 추가 상태 관리
  const handleShareSelect = (users: UserData[]) => {
    const updated: UserData[] = users;
    setFormData((prev) => ({ ...prev, selectedMembers: updated }));
    setSelectedUsers(users);
  };

  // 공유 받은 사람 제거 상태 관리
  const handleShareRemove = (updatedUsers: UserData[]) => {
    setFormData((prev) => ({ ...prev, selectedMembers: updatedUsers }));
    setSelectedUsers(updatedUsers);
  };

  return (
    <>
      <Form variant="vertical" className="form-field" onSubmit={() => { }}>
        <FormItem
          label="대화스타터"
          desc="대화스타터 입력은 최대 5개까지 가능해요"
          info={
            <Tooltip
              size="large"
              position={['bottom', 'end']}
              triggerEvent="click"
              className="form-tooltip"
              message={
                <div className="tooltip-head">
                  <Heading size="body1">대화 스타터</Heading>
                  <Text>
                    대화 스타터는 에이전트 선택 시 사용자가 <br /> 어떤 질문을 할 수 있는지에 대한 예시로 사용됩니다.
                  </Text>
                </div>
              }
            />
          }
        >
          <div style={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <label htmlFor="abclab-suggested-questions-switch" style={{ cursor: 'pointer' }}>
              <Text accent="alternative">ABC Lab 대화시작 질문 사용하기</Text>
            </label>
            <Switch
              id="abclab-suggested-questions-switch"
              checked={!(formData.aiOnUIConnectData?.useWorksAIConversationStarters ?? true)}
              onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                const checked = e.target.checked;
                setFormData((prev: IPluginPromptData) => ({
                  ...prev,
                  aiOnUIConnectData: {
                    ...(prev.aiOnUIConnectData ?? {}),
                    useWorksAIConversationStarters: !checked,
                  },
                }));
              }}
            />
          </div>
          {(() => {
            const isAbcLabMode = !(formData.aiOnUIConnectData?.useWorksAIConversationStarters ?? true);
            const starters = isAbcLabMode
              ? (formData.aiOnUIConnectData?.conversationStarters ?? [])
              : (formData.conversationStarters ?? []);

            if (isAbcLabMode && starters.length === 0) {
              return (
                <div className="dsx-cont-box" style={{ textAlign: 'center', padding: '24px' }}>
                  <Text variant="secondary">ABC Lab에 등록된 제안 질문이 없습니다.</Text>
                </div>
              );
            }

            return (
              <>
                <div className="dsx-cont-box">
                  {starters.map((question, index) => (
                    <div key={index} className="form-questions">
                      <Badge size="large">대화스타터 {index + 1}</Badge>
                      <div className="question-input">
                        <Input
                          size="xlarge"
                          placeholder="내용을 입력해 주세요."
                          maxLength={40}
                          showCount
                          full
                          value={question}
                          disabled={isAbcLabMode}
                          onChange={(e) => {
                            if (!isAbcLabMode) handleQuestionChange(index, e.target.value);
                          }}
                        />
                        {!isAbcLabMode && (
                          <IconButton
                            name="closeFill"
                            size="large"
                            className="q-del"
                            onClick={() => {
                              removeQuestion(index);
                            }}
                            disabled={(formData.conversationStarters?.length ?? 0) <= 1}
                          >
                            대화스타터 {index + 1} 삭제
                          </IconButton>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
                {!isAbcLabMode && (
                  <ButtonArea align="end">
                    <Button
                      size="large"
                      variant="secondary"
                      prefixIcon="plusFill"
                      onClick={addQuestion}
                      disabled={(formData.conversationStarters?.length ?? 0) >= 5}
                    >
                      대화스타터 추가
                    </Button>
                  </ButtonArea>
                )}
              </>
            );
          })()}
        </FormItem>
        <FormItem label="공개범위" isRequired>
          <RadioGroup size="large" options={options} value={formData.openScope} onChange={handleRadioChange} />
        </FormItem>
        {formData.openScope != EOpenScopeType.Private && (
          <FormItem
            label="기여자"
            desc="기여자 추가는 최대 30명까지 가능해요. 기여자도 에이전트를 같이 관리 할 수 있어요."
          >
            <UserSelector
              disabledUsers={disabledUsers}
              selectedUsers={managedUsers}
              onUserSelect={handleManageSelect}
              onUserRemove={handleManageRemove}
              buttonLabel="기여자 추가"
              disabled={authority != AuthorityType.Owner}
              maxUsers={30}
              showCount
            />
          </FormItem>
        )}
        {formData.openScope == EOpenScopeType.Part && (
          <FormItem
            label="공유 받는 사람"
            desc="공유 받은 사람만 에이전트를 사용할 수 있어요. 기여자에게는 자동으로 공유됩니다."
          >
            <UserSelector
              disabledUsers={disabledShare}
              selectedUsers={selectedUsers}
              onUserSelect={handleShareSelect}
              onUserRemove={handleShareRemove}
              buttonLabel="공유 받는 사람 추가"
              disabled={false}
              maxUsers={40}
              showCount
            />
          </FormItem>
        )}
        <FormItem label="해시태그" desc="해시태그 입력은 최대 3개까지 가능해요">
          <InputHashtag
            size="xlarge"
            variant="filled"
            placeholder="단어 입력 후 엔터를 통해 해시 태그를 입력해 주세요"
            name="tags"
            value={formData.tags ?? []}
            onChange={handleHashtagChange}
            maxTags={3}
            showCount
          />
        </FormItem>
      </Form>
    </>
  );
};

export default AionuCreateReg;
